import React, { useCallback, useMemo } from "react";
import ImageGallery, { ImageGalleryImage } from "../../../components/ImageGallery";
import ObjectDetailsFailure from "../common/components/ObjectDetailsFailure";
import { useTranslation } from "react-i18next";
import { getLocalizedText } from "../../../util/localizedText";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setIsMapOpen } from "../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { formatCurrency } from "../../../util";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { spaceOf } from "../../../util/useCases";
import { useBookingFlowService } from "../common/useBookingFlowService";
import PriceTagMap from "../common/components/ApartmentRental/PriceTagMap";
import { OwnRoomPin, roomIsPinned, withOwnRoom } from "../common/components/ApartmentRental/ownRoomMarker";
import FullScreenDialog from "../../common/FullScreenDialog";
import { PriceTagMarker } from "../common/components/ApartmentRental/PriceTagMap";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "../../../components/ui/accordion";
import MapView from "../common/components/ApartmentRental/MapView";
import { mapEmbedSrc } from "../../../util/mapEmbed";
import { useEnsureApartmentDetails } from "../common/hooks/useEnsureApartmentDetails";
import { resolveVenueRates } from "./venueRates";
import { roomGalleryImages } from "../common/util/galleryImages";

const ApartmentOverview = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const { selectedApartment, isMapOpen, locationApartmentList } = useSelector(
        (state: RootState) => state.apartment
    );
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

    const { data, isLoading, isError } = useEnsureApartmentDetails();

    // Which flow this is comes from the flow, not from per-tab storage —
  // see `useBookingFlowService` (QA #2764 E2).
  // The event flow's overview (sqm, kitchen …) or the stay flow's (beds, baths).
  const isEventFlow = spaceOf(useBookingFlowService())?.flow === "event";

    const handleOpenMap = useCallback(() => {
        if (!data) return;
        dispatch(setIsMapOpen(true));
    }, [data, dispatch]);

    // The room itself, from its own address: its map must show it even when
    // there are no search results to pin (a direct link, an empty search).
    const ownRoom = useMemo<OwnRoomPin | null>(() => (data
        ? {
            id: data.id,
            label: data.name,
            lat: data.Group?.location?.address?.geoLat,
            lng: data.Group?.location?.address?.geoLng,
        }
        : null), [data]);

    const resultMarkers = useMemo<PriceTagMarker[]>(() => {
        if (!Array.isArray(locationApartmentList)) {
            return [];
        }

        const selectedId = selectedApartment?.assetId || selectedApartment?.id || null;

        return locationApartmentList
            .filter(
                (apt) =>
                    typeof apt?.geoLat === "number" &&
                    typeof apt?.geoLng === "number" &&
                    !Number.isNaN(apt.geoLat) &&
                    !Number.isNaN(apt.geoLng)
            )
            .map((apt) => {
                const priceValue = Number(apt?.price ?? 0);
                const hasPrice = !Number.isNaN(priceValue) && priceValue > 0;
                const symbol = tenantConfig?.currencySymbol ?? "";
                const formattedNumeric = priceValue.toLocaleString(i18n.language, {
                    minimumFractionDigits: priceValue % 1 === 0 ? 0 : 2,
                    maximumFractionDigits: 2,
                });

                const label = hasPrice
                    ? symbol
                        ? `${symbol}${formattedNumeric}`
                        : formatCurrency(priceValue, i18n.language)
                    : t("apartment.price_on_request");

                return {
                    id: apt.assetId || apt.id,
                    lat: apt.geoLat,
                    lng: apt.geoLng,
                    label,
                    price: hasPrice ? priceValue : null,
                    isSelected: selectedId ? selectedId === (apt.assetId || apt.id) : false,
                };
            });
    }, [i18n.language, locationApartmentList, selectedApartment, t, tenantConfig?.currencySymbol]);
    const markers = useMemo(() => withOwnRoom(resultMarkers, ownRoom), [resultMarkers, ownRoom]);

    // The embedded map of the address when Google's script is absent OR this
    // room has no pin: the interactive map would be grey, or centred on the
    // neighbours (ownRoomMarker.ts › roomIsPinned).
    const shouldShowStaticMap =
        typeof window === "undefined" || typeof (window as any).google === "undefined"
        || !roomIsPinned(markers, ownRoom);
    const staticMapSrc = mapEmbedSrc(data?.Group?.location?.address);

    // A failed load is not an object without photos or description (#2764).
    if (isError) {
        return <ObjectDetailsFailure />;
    }

    return (
        <div className="max-w-full mx-auto flex flex-col md:max-w-[80%] p-2">
            <div className="w-full">
                <ImageGallery
                    loading={isLoading}
                    images={
                        roomGalleryImages(data).map((src) => ({
                            src,
                        })) as ImageGalleryImage[]
                    }
                    aspectRatio="16/9"
                    className="border border-gray-200"
                />
            </div>

            <div className="mt-2 text-xs">
                <h1 className="font-semibold text-lg mb-2 text-foreground">{data?.name}</h1>

                <div className="flex justify-between items-center mb-4 text-xs">
                    <div className="flex flex-wrap gap-x-2 font-semibold text-xs text-gray-600">
                        {/* Each fact renders only when it HAS a value. These two were
                            unconditional: optional-chained enough not to throw, but a
                            venue with no `accomodationdetail` printed the bare labels —
                            literally "guests  • room ( sqm)" with nothing in front of
                            them. `divideRoom` (#1714) creates a segment's object without
                            one, so that is a real state a customer reaches, not a
                            hypothetical. Absent facts are now simply not claimed. */}
                        <>
                            {data?.accomodationdetail?.guests != null && (
                                <span>
                                    {data.accomodationdetail.guests}{" "}
                                    {t("venue_reservation.guests")}
                                </span>
                            )}
                            {data?.accomodationdetail?.rooms != null && (
                                <span>
                                    • {data.accomodationdetail.rooms}{" "}
                                    {t("apartment.room", {
                                        count: data.accomodationdetail.rooms ?? 1,
                                    })}
                                    {isEventFlow &&
                                        data?.accomodationdetail?.sqm != null && (
                                        <>
                                            {" "}
                                            ({data.accomodationdetail.sqm} {t("apartment.sqm")}){" "}
                                        </>
                                    )}
                                </span>
                            )}
                            {!isEventFlow && (
                                <>
                                    <span>
                                        • {data?.accomodationdetail?.beds}{" "}
                                        {t("venue_reservation.beds")}
                                    </span>
                                    <span>
                                        • {data?.accomodationdetail?.baths}{" "}
                                        {t("venue_reservation.baths")}
                                    </span>
                                </>
                            )}
                        </>

                        {isEventFlow && (
                            <>
                                {(data?.accomodationdetail?.kitchen ?? 0) > 0 && (
                                    <span>
                                        • {data?.accomodationdetail?.kitchen}{" "}
                                        {t("apartment.kitchen")}
                                    </span>
                                )}

                                {(data?.accomodationdetail?.terrace ?? 0) > 0 && (
                                    <span>
                                        • {data?.accomodationdetail?.terrace}{" "}
                                        {t("apartment.terrace")}
                                    </span>
                                )}

                                {(data?.accomodationdetail?.garden ?? 0) > 0 && (
                                    <span>
                                        • {data?.accomodationdetail?.garden} {t("apartment.garden")}
                                    </span>
                                )}

                                {/* Only claim "no outdoor space" for a venue that HAS
                                    details. Without the first clause this asserted it of
                                    every venue that simply has none recorded, which is a
                                    different statement. */}
                                {data?.accomodationdetail != null &&
                                    (data?.accomodationdetail?.terrace ?? 0) === 0 &&
                                    (data?.accomodationdetail?.garden ?? 0) === 0 && (
                                        <span>
                                            • {data?.accomodationdetail?.outdoor_space}{" "}
                                            {t("apartment.no_outdoor_space")}
                                        </span>
                                    )}
                            </>
                        )}
                    </div>
                </div>

                {/* Daily and Weekend Price Section */}
                {data?.Group?.pricecategory?.pricescale && (
                    <div className="mb-4 text-sm text-gray-700">
                        {(() => {
                            // Same lookup as the booking summary and as the
                            // server's `computePricing.ts`, so the three cannot
                            // drift apart again (#2630 C3). Behaviour here is
                            // unchanged: the `||` is deliberate — a venue that
                            // prices only one day class cannot price a stay
                            // spanning the other, so it is on request.
                            const rates = resolveVenueRates(
                                data.Group.pricecategory.pricescale
                            );
                            const daily = rates.weekday;
                            const weekend = rates.weekend;

                            const isPriceOnRequest =
                                (daily ?? 0) <= 0 || (weekend ?? 0) <= 0;

                            return (
                                <>
                                    {isPriceOnRequest ? (
                                        <div>
                                            <span className="font-bold">
                                                {t("apartment.price_on_request")}
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            {daily !== null && daily > 0 && (
                                                <div>
                                                    <span className="font-normal">
                                                        {t(
                                                            "book_location.venue_reservation_overview.daily_price"
                                                        )}
                                                        :{" "}
                                                    </span>
                                                    <span className="font-bold">
                                                        {t("general.from")}{" "}
                                                        {formatCurrency(daily, i18n.language)}
                                                    </span>
                                                </div>
                                            )}
                                            {weekend !== null && weekend > 0 && (
                                                <div>
                                                    <span className="font-normal">
                                                        {t(
                                                            "book_location.venue_reservation_overview.weekend_price"
                                                        )}
                                                        :{" "}
                                                    </span>
                                                    <span className="font-bold">
                                                        {t("general.from")}{" "}
                                                        {formatCurrency(
                                                            weekend,
                                                            i18n.language
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                <h2 className="font-bold text-foreground text-sm leading-6 mb-1">
                    {t("book_location.venue_reservation_overview.our_facilities")}
                </h2>
                <p className="text-gray-700 mb-3 font-normal text-sm leading-5">
                    {getLocalizedText(data?.accomodationdetail?.facilityDescriptions)}
                </p>

                {(() => {
                    const note = data?.accomodationdetail?.importantNotes;
                    if (!note) return null;
                    const content = getLocalizedText(note);
                    return content.length > 0 ? (
                        <>
                            <h3 className="font-bold text-foreground text-sm leading-6 mb-1">
                                {t("book_location.venue_reservation_overview.important_guidelines")}
                            </h3>
                            <p className="text-gray-700 mb-3 font-normal text-sm leading-5">
                                {content}
                            </p>
                        </>
                    ) : null;
                })()}

                {(() => {
                    const visible = (data?.accomodationdetail?.policies ?? [])
                        .map((policy) => ({
                            title: getLocalizedText(policy.titles) || policy.title,
                            description: getLocalizedText(policy.descriptions) || policy.description,
                        }))
                        .filter((policy) => policy.title?.length || policy.description?.length);
                    return visible.length > 0 ? (
                        <>
                            <h3 className="font-bold text-foreground text-sm leading-6 mb-1">
                                {t("apartment.policies_title")}
                            </h3>
                            <Accordion type="single" collapsible className="mb-3">
                                {visible.map((policy, index) => (
                                    <AccordionItem key={index} value={`policy-${index}`}>
                                        <AccordionTrigger className="text-sm text-foreground text-left py-3">
                                            {policy.title || t("apartment.policies_title")}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-sm text-gray-700 whitespace-pre-line">
                                            {policy.description}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </>
                    ) : null;
                })()}

                <h3 className="font-bold text-foreground text-sm leading-6 mb-1">
                    {t("apartment.location")}
                </h3>
                <div className="w-full h-[200px] border border-gray-200 rounded-lg overflow-hidden mb-3 relative group">
                    {shouldShowStaticMap ? (
                        staticMapSrc && (
                            <iframe
                                title="Property Location"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                src={staticMapSrc}
                                allowFullScreen
                            />
                        )
                    ) : (
                        <PriceTagMap
                            markers={markers}
                            className="absolute inset-0"
                            mapOptions={{
                                gestureHandling: "none",
                                zoom: 13,
                            }}
                            fitBoundsPadding={{ top: 32, right: 32, bottom: 32, left: 32 }}
                            zoomOnSingleMarker={15}
                        />
                    )}
                    <button
                        type="button"
                        onClick={handleOpenMap}
                        className="absolute inset-0 flex items-end justify-between bg-gradient-to-t from-black/70 via-black/10 to-transparent p-4 text-white transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary group-hover:opacity-100 cursor-pointer"
                        aria-label={t("apartment.map_view") ?? "Map view"}
                    >
                        <span className="text-left text-sm font-medium">
                            {t("book_location.venue_reservation_overview.view_map_fullscreen", {
                                defaultValue: t("apartment.map_view"),
                            })}
                        </span>
                        <span className="ml-3 text-lg" aria-hidden="true">
                            ↗
                        </span>
                    </button>
                </div>
            </div>
            <FullScreenDialog
                open={isMapOpen}
                className="max-w-full bg-transparent"
                onClose={() => dispatch(setIsMapOpen(false))}
                ariaLabel={t("common.dialog.map")}
            >
                <MapView ownRoom={ownRoom} fallbackAddress={data?.Group?.location?.address} />
            </FullScreenDialog>
            <LoaderOverlay open={isLoading} />
        </div>
    );
};

export default ApartmentOverview;
